import { GetPageByRouteQuery } from "@eurodrive/apollo"
import { extractProperty, getBreadCrumbs } from "@eurodrive/utils"

export type RichTextSection = {
  title: string
  content: string
}

export type RichTextPageContent = {
  /** Page title — the first block's title, rendered as the h1. */
  title: string
  sections: RichTextSection[]
}

/**
 * Reads the `titleAndText` blocks of a `contentPage` node.
 *
 * `contentPage` has no dedicated title/description property, so the first block's title is
 * the page title. Any further blocks contribute their own section heading. Other block
 * types are ignored — this template only renders rich text.
 */
export function getRichTextPageContent(
  data: GetPageByRouteQuery,
): RichTextPageContent {
  const blocks = extractProperty(data.page, "blocks")
  const sections: RichTextSection[] = (Array.isArray(blocks) ? blocks : [])
    .filter((block: any) => block?.blockType === "titleAndText")
    .map((block: any) => ({
      title: extractProperty(block, "title") || "",
      content: extractProperty(block, "content") || "",
    }))

  return {
    title: sections[0]?.title || "",
    sections,
  }
}

/** False for an unpublished language variant, which must 404 rather than render empty. */
export function hasRichTextContent(data: GetPageByRouteQuery): boolean {
  const { sections } = getRichTextPageContent(data)
  return sections.some((section) => section.title || section.content)
}

/**
 * Breadcrumbs with a usable label for the current page. The CMS leaves titleBreadcrumbs and
 * metaTitle empty on these nodes, and getBreadCrumbs then falls back to the English node
 * name ("Accessibility Statement") — use the page title instead.
 */
export function getRichTextBreadcrumbs(
  data: GetPageByRouteQuery,
  locale: string,
) {
  const crumbs = getBreadCrumbs(data, locale)
  const { title } = getRichTextPageContent(data)

  return {
    ...crumbs,
    selfObject: {
      ...crumbs.selfObject,
      title:
        extractProperty(data.page, "titleBreadcrumbs") ||
        extractProperty(data.page, "metaTitle") ||
        title ||
        crumbs.selfObject.title,
    },
  }
}
